<template>
  <component
    :is="tag"
    :id="groupId"
    :class="ns.b('group')"
    role="group"
    :aria-label="
      !isLabeledByFormItem ? ariaLabel || 'checkbox-group' : undefined
    "
    :aria-labelledby="isLabeledByFormItem ? formItem?.labelId : undefined"
  >
    <slot>
      <component
        :is="optionComponent"
        v-for="(item, index) in options"
        :key="index"
        v-bind="getOptionProps(item)"
      />
    </slot>
  </component>
</template>

<script lang="ts" setup>
import { computed, nextTick, provide, toRefs, watch } from 'vue'
import { isEqual, omit, pick } from 'lodash-unified'
import { CHANGE_EVENT, UPDATE_MODEL_EVENT } from '@zoryn/constants'
import { NOOP } from '@zoryn/utils'
import { useNamespace } from '@zoryn/hooks'
import {
  useFormDisabled,
  useFormItem,
  useFormItemInputId,
} from '@zoryn/components/form'
import { checkboxDefaultProps, checkboxGroupEmits } from './checkbox-group'
import { checkboxGroupContextKey } from './constants'
import ZCheckbox from './checkbox.vue'
import ZCheckboxButton from './checkbox-button.vue'

import type {
  CheckboxGroupProps,
  CheckboxGroupValueType,
} from './checkbox-group'

defineOptions({
  name: 'ZCheckboxGroup',
})

const props = withDefaults(defineProps<CheckboxGroupProps>(), {
  modelValue: () => [],
  disabled: undefined,
  tag: 'div',
  validateEvent: true,
  props: () => checkboxDefaultProps,
  type: 'checkbox',
})
const emit = defineEmits(checkboxGroupEmits)
const ns = useNamespace('checkbox')

const checkboxDisabled = useFormDisabled()
const { formItem } = useFormItem()
const { inputId: groupId, isLabeledByFormItem } = useFormItemInputId(props, {
  formItemContext: formItem,
})

const changeEvent = async (value: CheckboxGroupValueType) => {
  emit(UPDATE_MODEL_EVENT, value)
  await nextTick()
  emit(CHANGE_EVENT, value)
}

const modelValue = computed({
  get() {
    return props.modelValue
  },
  set(val: CheckboxGroupValueType) {
    changeEvent(val)
  },
})

const aliasProps = computed(() => ({
  ...checkboxDefaultProps,
  ...props.props,
}))
const getOptionProps = (option: Record<string, any>) => {
  const { label, value, disabled } = aliasProps.value
  const base = {
    label: option[label],
    value: option[value],
    disabled: option[disabled],
  }
  return { ...omit(option, [label, value, disabled]), ...base }
}

const optionComponent = computed(() =>
  props.type === 'button' ? ZCheckboxButton : ZCheckbox
)

provide(checkboxGroupContextKey, {
  ...pick(toRefs(props), [
    'size',
    'min',
    'max',
    'validateEvent',
    'fill',
    'textColor',
  ]),
  disabled: checkboxDisabled,
  modelValue,
  changeEvent,
})

watch(
  () => props.modelValue,
  (newVal, oldValue) => {
    if (props.validateEvent && !isEqual(newVal, oldValue)) {
      formItem?.validate('change').catch(NOOP)
    }
  }
)
</script>
